import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';

interface StatusCount {
  key: string;
  label: string;
  count: number;
  color: string;
}

interface OpsLink {
  path: string;
  label: string;
  icon: string;
  count: number;
  amount?: number | null;
}

@Component({
  selector: 'app-b2b-operational-dashboard',
  templateUrl: './b2b-operational-dashboard.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-operational-dashboard.component.css']
})
export class B2bOperationalDashboardComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  isLoading = false;

  vehicleStatus: StatusCount[] = [];
  totalVehicles = 0;
  links: OpsLink[] = [];
  openClaims = 0;
  pendingCharges = 0;
  pendingChargesAmount = 0;
  unpaidBilling = 0;
  unpaidBillingAmount = 0;
  activeReservations = 0;
  scheduledOs = 0;
  totalOs = 0;
  openOs = 0;
  accessoriesCount = 0;
  trafficFinesCount = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.load();
      }
    });
  }

  load(): void {
    if (!this.contractId) return;
    this.isLoading = true;

    forkJoin({
      contract: this.contractsService.getB2bContractById(this.contractId).pipe(catchError(() => of(null))),
      claims: this.contractsService.getB2bClaims(this.contractId).pipe(catchError(() => of([]))),
      charges: this.contractsService.getB2bCharges(this.contractId).pipe(catchError(() => of([]))),
      trafficFines: this.contractsService.getB2bTrafficFines(this.contractId).pipe(catchError(() => of([]))),
      reservations: this.contractsService.getB2bReservations(this.contractId).pipe(catchError(() => of([]))),
      accessories: this.contractsService.getB2bAccessories(this.contractId).pipe(catchError(() => of([]))),
      billing: this.contractsService.getB2bBillingPeriods(this.contractId).pipe(catchError(() => of([]))),
      serviceOrders: this.contractsService.getB2bServiceOrders(this.contractId).pipe(catchError(() => of([]))),
      vehicles: this.contractsService.listAllB2bContractVehicles(this.contractId).pipe(
        catchError(() => of([]))
      )
    }).subscribe({
      next: (res) => {
        this.contractNumber = res.contract?.contractNumber || '';
        this.contractName = res.contract?.contractName || '';

        const claims = (res.claims || []).map((x: any) => this.pick(x));
        const charges = (res.charges || []).map((x: any) => this.pick(x));
        const trafficFines = (res.trafficFines || []).map((x: any) => this.pick(x));
        const reservations = (res.reservations || []).map((x: any) => this.pick(x));
        const accessories = (res.accessories || []).map((x: any) => this.pick(x));
        const billing = (res.billing || []).map((x: any) => this.pick(x));
        const vehicles = (res.vehicles || []).map((x: any) => this.pick(x));
        const serviceOrders = (res.serviceOrders || []).map((x: any) => this.pick(x));

        this.openClaims = claims.filter((c: any) => {
          const s = String(c.status || '').toLowerCase();
          return s === 'open' || s === 'inanalysis';
        }).length;

        const pendingChargeList = charges.filter((c: any) => String(c.status || '').toLowerCase() === 'pending');
        this.pendingCharges = pendingChargeList.length;
        this.pendingChargesAmount = pendingChargeList.reduce((sum: number, c: any) => sum + (Number(c.amount) || 0), 0);

        this.trafficFinesCount = trafficFines.length;

        this.activeReservations = reservations.filter((r: any) => {
          const s = String(r.status || '').toLowerCase();
          return s === 'requested' || s === 'confirmed' || s === 'inuse';
        }).length;

        this.accessoriesCount = accessories.length;

        const unpaid = billing.filter((b: any) => !(b.isPaid ?? b.IsPaid));
        this.unpaidBilling = unpaid.length;
        this.unpaidBillingAmount = unpaid.reduce((sum: number, b: any) => sum + (Number(b.amount) || 0), 0);

        this.totalOs = serviceOrders.length;
        this.scheduledOs = serviceOrders.filter((o: any) => !!(o.isScheduled ?? o.IsScheduled)).length;
        this.openOs = serviceOrders.filter((o: any) => {
          const s = String(o.status || '').toLowerCase();
          return s === 'aberto' || s === 'em andamento' || s === 'open';
        }).length;
        this.buildVehicleStatus(vehicles);

        this.links = [
          { path: 'manutencoes', label: 'OS / manutenções', icon: 'fas fa-wrench', count: this.totalOs },
          { path: 'sinistros', label: 'Sinistros abertos', icon: 'fas fa-car-crash', count: this.openClaims },
          { path: 'multas', label: 'Multas', icon: 'fas fa-gavel', count: this.trafficFinesCount },
          { path: 'cobrancas', label: 'Cobranças pendentes', icon: 'fas fa-file-invoice-dollar', count: this.pendingCharges, amount: this.pendingChargesAmount },
          { path: 'reservas', label: 'Reservas ativas', icon: 'fas fa-calendar-check', count: this.activeReservations },
          { path: 'acessorios', label: 'Acessórios no catálogo', icon: 'fas fa-toolbox', count: this.accessoriesCount },
          { path: 'pagamentos', label: 'Competências em aberto', icon: 'fas fa-money-check-alt', count: this.unpaidBilling, amount: this.unpaidBillingAmount }
        ];

        this.isLoading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
      }
    });
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  goTo(path: string): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, path]);
  }

  formatMoney(value: number | null | undefined): string {
    if (value == null) return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  private buildVehicleStatus(vehicles: any[]): void {
    const buckets: Record<string, number> = {
      ATIVO: 0,
      INATIVO: 0,
      MANUTENCAO: 0,
      SINISTRADO: 0,
      OUTROS: 0
    };

    vehicles.forEach(v => {
      const raw = String(v.operationalStatus || v.OperationalStatus || 'ATIVO').toUpperCase();
      if (Object.prototype.hasOwnProperty.call(buckets, raw) && raw !== 'OUTROS') {
        buckets[raw] = (buckets[raw] || 0) + 1;
      } else {
        buckets['OUTROS'] = (buckets['OUTROS'] || 0) + 1;
      }
    });

    this.totalVehicles = vehicles.length;
    this.vehicleStatus = [
      { key: 'ATIVO', label: 'Ativos', count: buckets['ATIVO'] || 0, color: '#10B981' },
      { key: 'INATIVO', label: 'Inativos', count: buckets['INATIVO'] || 0, color: '#6B7280' },
      { key: 'MANUTENCAO', label: 'Manutenção', count: buckets['MANUTENCAO'] || 0, color: '#F59E0B' },
      { key: 'SINISTRADO', label: 'Sinistrados', count: buckets['SINISTRADO'] || 0, color: '#EF4444' }
    ];
    if ((buckets['OUTROS'] || 0) > 0) {
      this.vehicleStatus.push({ key: 'OUTROS', label: 'Outros', count: buckets['OUTROS'] || 0, color: '#3B82F6' });
    }
  }

  private pick(dto: any): any {
    if (!dto || typeof dto !== 'object') return dto;
    return {
      ...dto,
      id: dto.id ?? dto.Id,
      status: dto.status ?? dto.Status,
      amount: dto.amount ?? dto.Amount,
      isPaid: dto.isPaid ?? dto.IsPaid,
      operationalStatus: dto.operationalStatus ?? dto.OperationalStatus
    };
  }
}
